import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import SearchBar from '../../../../ui/inputs/SearchBar';
import JSZip from 'jszip';
import fileDownload from 'js-file-download';

import mersenne001FactsheetPdf from '../static_files/factsheet/mersenne001_factsheet.pdf';
import mersenne002FactsheetPdf from '../static_files/factsheet/mersenne002_factsheet.pdf';
import mersenne003FactsheetPdf from '../static_files/factsheet/mersenne003_factsheet.pdf';
import mersenne004FactsheetPdf from '../static_files/factsheet/mersenne004_factsheet.pdf';
import mersenne005FactsheetPdf from '../static_files/factsheet/mersenne005_factsheet.pdf';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { RaidrRoutePaths } from '../../../../../routes/page-routes';

// This is the initial Fact Sheet demo viewer. It currently has a static list of funds, but in the future we will have a dynamic list of funds from the API.

const STATIC_DEMO_FUNDS = [
  {
    id: 'mersenne001',
    name: 'Mersenne Scala Fund',
  },
  {
    id: 'mersenne002',
    name: 'Mersenne Global Long / Short Equity Fund',
  },
  {
    id: 'mersenne003',
    name: 'Mersenne Global Sustainable Value Fund',
  },
  {
    id: 'mersenne004',
    name: 'Mersenne Global Defensive Fund',
  },
  {
    id: 'mersenne005',
    name: 'Mersenne Credit UCITS Fund',
  },
];

const FactSheetsOverview: FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const filteredFunds = STATIC_DEMO_FUNDS.filter((fund) => {
    return (
      fund.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fund.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const headers = ['Fund ID', 'Fund Name', 'View Version'];

  return (
    <div className="h-full rounded-2xl border border-gray-200 bg-white">
      <FactSheetHeader setSearchTerm={setSearchTerm} />
      <div className="w-full overflow-x-auto">
        <table className="w-full table-fixed rounded-lg border border-gray-200 shadow-sm">
          <thead className="hidden md:table-header-group">
            <tr>
              {headers.map((header) => (
                <th
                  key={header}
                  className="w-1/3 border-b border-mainblue p-2 text-center text-sm font-bold text-mainblue md:p-5 md:text-base"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredFunds.map((fund) => (
              <tr
                key={fund.id}
                className="mb-4 block rounded border border-gray-200 p-3 shadow-sm md:table-row md:p-0"
              >
                {/* Fund ID */}
                <td
                  className="flex justify-between p-2 text-sm md:table-cell md:p-3 md:text-center"
                  data-label="Fund ID"
                >
                  <span className="font-medium md:hidden">Fund ID:</span>
                  {fund.id}
                </td>

                {/* Fund Name */}
                <td
                  className="flex justify-between p-2 text-sm md:table-cell md:p-3 md:text-center"
                  data-label="Fund Name"
                >
                  <span className="font-medium md:hidden">Fund Name:</span>
                  {fund.name}
                </td>

                <td
                  className="flex justify-between p-2 text-sm md:table-cell md:p-3 md:text-center"
                  data-label="Fund Name"
                >
                  <span className="font-medium md:hidden">View Version:</span>

                  <div className="flex items-center justify-center">
                    <Link
                      to={`${RaidrRoutePaths.FACT_SHEETS_VIEW}?fundId=${fund.id}`}
                    >
                      <VisibilityIcon className="aspect-square h-8 w-8 cursor-pointer rounded bg-mainblue fill-white p-1 text-lg text-white hover:bg-sky-800 focus:bg-sky-900" />
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FactSheetsOverview;

interface FactSheetHeaderProps {
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

const FactSheetHeader: FC<FactSheetHeaderProps> = ({ setSearchTerm }) => {
  const handleDownloadAll = async () => {
    try {
      const zip = new JSZip();

      // Array of PDF files with their names
      const pdfFiles = [
        { url: mersenne001FactsheetPdf, name: 'mersenne001_factsheet.pdf' },
        { url: mersenne002FactsheetPdf, name: 'mersenne002_factsheet.pdf' },
        { url: mersenne003FactsheetPdf, name: 'mersenne003_factsheet.pdf' },
        { url: mersenne004FactsheetPdf, name: 'mersenne004_factsheet.pdf' },
        { url: mersenne005FactsheetPdf, name: 'mersenne005_factsheet.pdf' },
      ];

      // Fetch all PDFs and add them to the zip
      const pdfPromises = pdfFiles.map(async ({ url, name }) => {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${name}`);
        }
        const blob = await response.blob();
        zip.file(name, blob);
      });

      await Promise.all(pdfPromises);

      // Generate zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download the zip file
      const fileName = `All_Fact_Sheets_${new Date().toISOString().split('T')[0]}.zip`;
      fileDownload(zipBlob, fileName);
    } catch (error) {
      console.error('Error creating zip file:', error);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg p-4 py-6 shadow-sm">
      {/* Left Section */}
      <div className="flex min-w-[250px] flex-wrap items-center gap-3">
        <div className="rounded bg-mainblue px-4 py-0.5 text-sm font-semibold text-white shadow">
          English
        </div>
        <h1 className="text-lg font-medium text-mainblue">
          Fact Sheets Overview
        </h1>
      </div>

      {/* Right Section */}
      <div className="flex w-full min-w-[250px] flex-wrap justify-between gap-3 md:w-fit md:items-center">
        <SearchBar
          searchHandler={setSearchTerm}
          className="w-full py-1 md:w-auto"
        />
        <button
          onClick={handleDownloadAll}
          className={`flex items-center justify-center gap-2 rounded-sm bg-mainblue px-3 py-1 text-center text-sm text-white transition hover:bg-sky-800 md:mx-auto`}
        >
          <span className="hidden font-bold md:inline">
            Download All Files
            <DownloadForOfflineIcon className="ml-3 text-[1.25rem]" />
          </span>
          <DownloadForOfflineIcon className="text-[1.25rem] md:hidden" />
        </button>
      </div>
    </div>
  );
};
